import { Injectable } from '@angular/core';
import { RiskLevelService } from './risk-level.service';
import { ProbabilityOfFailureService } from './probability-of-failure.service';
import { ConsequenceService } from './consequence.service';
import { Observable, catchError, firstValueFrom, forkJoin, map, of, tap } from 'rxjs';
import { ConsequenceItem, ProbabilityOfFailureItem, RiskLevelItem, RiskMatrix } from '../types/risk-config.model';
import { HttpClient } from '@angular/common/http';
import { environment } from 'src/environments/environment';

export interface FetchRiskMatrixResponse {
  riskMatrixData: RiskMatrix;
  riskLevelItems: RiskLevelItem[];
  probabilityOfFailureItems: ProbabilityOfFailureItem[];
  consequenceItems: ConsequenceItem[];
}
@Injectable({
  providedIn: 'root'
})
export class RiskMatrixService {

  private cache: Map<string, RiskMatrix> = new Map();
  private dataLoaded = false

  constructor(
    private riskLevelService: RiskLevelService,
    private probabilityOfFailureService: ProbabilityOfFailureService,
    private consequenceService: ConsequenceService,
    private http: HttpClient
  ) {}

  fetchRiskMatrixData(customerId: string): Observable<FetchRiskMatrixResponse> {
    const riskMatrixDataObs = this.getRiskMatrix(customerId)
    const riskLevelsObs = this.riskLevelService.getRiskLevelItems(customerId);
    const probabilityOfFailureObs = this.probabilityOfFailureService.getProbabilityOfFailureItems(customerId);
    const consequenceObs = this.consequenceService.getConsequenceItems(customerId);

    return forkJoin({
      riskMatrixData: riskMatrixDataObs.pipe(catchError(error => {
        console.error('Error fetching risk matrix data:', error);
        return of(null);
      })),
      riskLevelItems: riskLevelsObs.pipe(catchError(error => {
        console.error('Error fetching risk levels:', error);
        return of([]);
      })),
      probabilityOfFailureItems: probabilityOfFailureObs.pipe(catchError(error => {
        console.error('Error fetching probability of failure:', error);
        return of([]);
      })),
      consequenceItems: consequenceObs.pipe(catchError(error => {
        console.error('Error fetching consequences:', error);
        return of([]);
      }))
    });
  }

  private readonly url = `${environment.API_INSPEC}RiskMatrix`

  getRiskMatrix(customerId: string): Observable<RiskMatrix> {
    return this.http.get<RiskMatrix>(this.url + `?customerId=${customerId}`);
  }

  getCachedRiskMatrix(customerId: string): Observable<RiskMatrix> {
    if (this.cache.has(customerId)) {
      console.log('Returning data from cache for customer:', customerId);
      return of(this.cache.get(customerId)!);
    }
    return this.fetchAndCacheRiskMatrix(customerId);
  }

  private fetchAndCacheRiskMatrix(customerId: string): Observable<RiskMatrix> {
    return this.http.get<RiskMatrix>(`${this.url}?customerId=${customerId}`).pipe(
      tap(item => {
        this.cache.set(customerId, item);
        this.dataLoaded = true;
      }),
      catchError(error => {
        console.error('Failed to fetch items', error);
        return of({} as RiskMatrix);
      })
    );
  }

  postRiskMatrix(riskMatrix: RiskMatrix) {
    return this.http.post<{ id: string }>(this.url, riskMatrix);
  }

  putRiskMatrix(riskMatrix: RiskMatrix) {
    return this.http.put(this.url, riskMatrix);
  }

  deleteRiskMatrix(id: string) {
    return this.http.delete<boolean>(this.url + `?id=${id}`);
  }
  
  addEmptyCellsForProbabilityOfFailureItemAsync(item: ProbabilityOfFailureItem) {
    return this.http.post(this.url + `/AddEmptyCellsForProbabilityOfFailureItemAsync`, item);
  }

  addEmptyCellsForConsequenceItemAsync(item: ConsequenceItem) {
    return this.http.post(this.url + `/AddEmptyCellsForConsequenceItemAsync`, item);
  }

  deleteCellsForConsequenceItemAsync(item: ConsequenceItem) {
    return this.http.post(this.url + `/DeleteCellsForConsequenceItemAsync`, item);
  }

  deleteCellsForProbabilityOfFailureItemAsync(item: ProbabilityOfFailureItem) {
    return this.http.post(this.url + `/DeleteCellsForProbabilityOfFailureItemAsync`, item);
  }
  

  createUniqueCellId(consequenceId: string, pofId: string): string {
    return `${consequenceId}__${pofId}`
  }

  fetchCachedRiskMatrixData(customerId: string): Observable<FetchRiskMatrixResponse> {
    const riskMatrixDataObs = this.getCachedRiskMatrix(customerId)
    const riskLevelsObs = this.riskLevelService.getCachedRiskLevelItems(customerId);
    const probabilityOfFailureObs = this.probabilityOfFailureService.getCachedPofItems(customerId);
    const consequenceObs = this.consequenceService.getCachedConsequenceItems(customerId);

    return forkJoin({
      riskMatrixData: riskMatrixDataObs.pipe(catchError(error => {
        console.error('Error fetching risk matrix data:', error);
        return of(null);
      })),
      riskLevelItems: riskLevelsObs.pipe(catchError(error => {
        console.error('Error fetching risk levels:', error);
        return of([]);
      })),
      probabilityOfFailureItems: probabilityOfFailureObs.pipe(catchError(error => {
        console.error('Error fetching probability of failure:', error);
        return of([]);
      })),
      consequenceItems: consequenceObs.pipe(catchError(error => {
        console.error('Error fetching consequences:', error);
        return of([]);
      }))
    });
  }

  async getRiskLevelData(pofId: string, consequenceId: string, customerId: string) {
    const fullRiskMatrix = await firstValueFrom(
      this.fetchCachedRiskMatrixData(customerId)
    );
    
    // Create cell ID using the existing helper method
    const cellId = this.createUniqueCellId(consequenceId, pofId);
    
    // Find the matching cell in the risk matrix
    const cell = fullRiskMatrix.riskMatrixData.cells.find(c => c.id === cellId);
    
    if (!cell) {
      return {
        color: '#000000',
        name: 'Unknown'
      };
    }
    
    // Find the risk level item that matches the cell's risk level ID
    const riskLevel = fullRiskMatrix.riskLevelItems.find(
      rl => rl.id === cell.riskLevelId
    );
    
    return {
      color: riskLevel?.color || '#000000',
      name: riskLevel?.name || 'Unknown'
    };
  }

}
